import React, { useState, useEffect } from 'react';
import { Button, Modal, Form, Input, InputNumber, DatePicker, Select, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import SelectSearchInput from '../../utils/components/selectSearchInput';
import moment from 'moment'
import axios from '../../services/axios'
import { ITINERAIRES_LIST } from '../../utils/constants/pays';

const { Option } = Select;

interface Values {
  title: string;
  description: string;
  modifier: string;
}

interface CollectionCreateFormProps {
  visible: boolean;
  onCreate: (values: Values) => void;
  onCancel: () => void;
  clients: any[];
  devises: any[];
  setClientVisible: () => void
}

const CollectionCreateForms: React.FC<CollectionCreateFormProps> = ({
  visible,
  onCreate,
  onCancel,
  clients,
  devises,
  setClientVisible
}) => {
  const [form] = Form.useForm();
  const [canEnterMontant, setCanEnterMontant] = useState(false);
  const [shouldRecalculate, setShouldRecalculate] = useState(false);
  const [searchingDepot, setSearchingDepot] = useState(false);
  const [depotFound, setDepotFound] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [montantRestant, setMontantRestant] = useState(0);
  const [depotInfo, setDepotInfo] = useState<any>(null);

  useEffect(() => {
    console.log('monted');
  }, [])

  useEffect(() => {
    if (shouldRecalculate) {
      const montant = form.getFieldValue('montant');
      const deviseId = form.getFieldValue('devise_id');
      const deviseSortie = form.getFieldValue('devise_sortie');
      const taux = form.getFieldValue('taux');
      
      if (montant && deviseId && deviseSortie && taux) {
        getAmountConvert();
      }
      setShouldRecalculate(false);
    }
  }, [shouldRecalculate]);

  const onReset = () => {
    form.resetFields();
    setCanEnterMontant(false);
    setDepotFound(false);
    setFormKey(0);
    setMontantRestant(0);
    setDepotInfo(null);
  }

  const handleCancel = () => {
    form.resetFields();
    setCanEnterMontant(false);
    setDepotFound(false);
    setFormKey(0);
    setMontantRestant(0);
    setDepotInfo(null);
    onCancel();
  }

  const config = {
    rules: [{ type: 'object' as const, required: true, message: 'Please select time!' }],
  };

  const onChange = (name: string, value: any) => {
    form.setFieldsValue({ [name]: value })
  }

  const onSearch = (val: any) => {
    
  }

  // Rechercher le dépôt par numéro de reçu
  const searchDepotByNumero = () => {
    const numeroRecu = form.getFieldValue('numero_recu_depot');
    
    if (!numeroRecu) {
      message.warning('Veuillez saisir un numéro de reçu');
      return;
    }

    setSearchingDepot(true);
    
    axios.get(`/operations/search/numero/${numeroRecu}`)
      .then((response) => {
        const depot = response.data;
        
        if (depot && depot.type_operation === 'depot') {
          const montantDisponible = depot.montant_restant || depot.montant_sortie;
          
          setDepotInfo(depot);
          setMontantRestant(montantDisponible);
          
          // Pré-remplir TOUS les champs avec les valeurs du dépôt
          form.setFieldsValue({
            'numero_recu': numeroRecu,
            'client_id': depot.client_id,
            'montant': montantDisponible,
            'pays_depart': depot.pays_depart,
            // Devise d'entrée = devise de sortie du dépôt (VERROUILLÉE)
            'devise_id': depot.devisesortie.code,
            // Devise de sortie = devise de sortie du dépôt (PRÉ-REMPLIE mais MODIFIABLE)
            'devise_sortie': depot.devisesortie.code,
            // Taux = 1 par défaut (PRÉ-REMPLI mais MODIFIABLE)
            'taux': 1,
            // Montant converti (AFFICHÉ)
            'montant_convert': montantDisponible,
            // Type de paiement du dépôt (PRÉ-REMPLI mais MODIFIABLE)
            'type_paiement': depot.type_paiement || 'espece',
            // Motif du dépôt (PRÉ-REMPLI mais MODIFIABLE)
            'motif': depot.motif || 'achat',
            // Pays de récupération du dépôt (PRÉ-REMPLI mais MODIFIABLE)
            'pays_recuperation': depot.pays_recuperation || 'CONAKRY',
          });
          
          setDepotFound(true);
          setFormKey(prev => prev + 1);
          
          if (depot.nombre_retraits > 0) {
            message.success({
              content: `Dépôt trouvé ! Montant initial: ${depot.montant_sortie.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${depot.devisesortie.code}, Déjà retiré: ${depot.total_retire.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${depot.devisesortie.code}, Reste: ${montantDisponible.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${depot.devisesortie.code}`,
              duration: 6,
            });
          } else {
            message.success(`Dépôt trouvé et chargé avec succès - Montant disponible: ${montantDisponible.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} ${depot.devisesortie.code}`);
          }
          
          // Activer la saisie du montant
          setCanEnterMontant(true);
        } else {
          message.error('Aucun dépôt trouvé avec ce numéro de reçu');
          setDepotFound(false);
          setMontantRestant(0);
          setDepotInfo(null);
        }
        
        setSearchingDepot(false);
      })
      .catch((error) => {
        console.log(error);
        
        if (error.response && error.response.status === 400) {
          const errorData = error.response.data;
          
          if (errorData.fully_withdrawn) {
            message.error({
              content: `Ce dépôt a été entièrement retiré ! Montant initial: ${errorData.depot_initial}, Total retiré: ${errorData.total_retire}`,
              duration: 6,
            });
          } else if (typeof errorData === 'string') {
            message.error(errorData);
          } else {
            message.error('Ce numéro de reçu ne correspond pas à un dépôt valide');
          }
        } else {
          message.error('Erreur lors de la recherche du dépôt');
        }
        
        setSearchingDepot(false);
        setDepotFound(false);
        setMontantRestant(0);
        setDepotInfo(null);
      });
  };

  const checkConversionFields = () => {
    const deviseId = form.getFieldValue('devise_id');
    const deviseSortie = form.getFieldValue('devise_sortie');
    const taux = form.getFieldValue('taux');
    
    if (deviseId && deviseSortie && taux) {
      setCanEnterMontant(true);
      return true;
    }
    return false;
  }

  const onDeviseChange = (name: string, value: any) => {
    onChange(name, value);
    setTimeout(() => {
      checkConversionFields();
      const montant = form.getFieldValue('montant');
      if (montant) {
        setShouldRecalculate(true);
      }
    }, 100);
  }

  const onTauxChange = (value: any) => {
    if (value) {
      checkConversionFields();
      const montant = form.getFieldValue('montant');
      if (montant && form.getFieldValue('devise_id') && form.getFieldValue('devise_sortie')) {
        setShouldRecalculate(true);
      }
    } else {
      setCanEnterMontant(false);
      form.setFieldsValue({ 
        'montant': null,
        'montant_convert': null 
      });
    }
  }

  const onMontantChange = (value: any) => {
    if (!value) {
      form.setFieldsValue({ 'montant_convert': null });
      return;
    }

    if (form.getFieldValue('devise_id') && form.getFieldValue('devise_sortie') && form.getFieldValue('taux')) {
      getAmountConvert();
    }
  }

  const getAmountConvert = () => {
    const data = {
      "devise_id": form.getFieldValue('devise_id'),
      "devise_sortie": form.getFieldValue('devise_sortie'),
      "montant": form.getFieldValue('montant'),
      "taux": form.getFieldValue('taux'),
    }
    axios.post(`operations/devises/convert`, data)
      .then((response) => {
        form.setFieldsValue({ 'montant_convert': response.data as number })
      }).catch((err) => {
        console.log(err);
      });
  }

  const validateMontant = (_: any, value: number) => {
    if (!value) {
      return Promise.reject('Le montant est requis');
    }
    if (depotFound && value > montantRestant) {
      return Promise.reject(`Le montant ne peut pas dépasser ${montantRestant.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`);
    }
    if (value <= 0) {
      return Promise.reject('Le montant doit être supérieur à 0');
    }
    return Promise.resolve();
  };

  return (
    <Modal
      visible={visible}
      title={<>Effectuer un nouveau Retrait <Button onClick={() => setClientVisible()} type="dashed" className=""> Nouveau Client </Button></>}
      okText="Enregistrer"
      cancelText="annuler"
      onCancel={handleCancel}
      onOk={() => {
        form
          .validateFields()
          .then(values => {
            form.resetFields();
            setCanEnterMontant(false);
            setDepotFound(false);
            setFormKey(0);
            setMontantRestant(0);
            setDepotInfo(null);
            onCreate(values);
          })
          .catch(info => {
            console.log('Validate Failed:', info);
          });
      }}
    >
      <Form
        key={formKey}
        form={form}
        layout="horizontal"
        name="form_in_modal"
        initialValues={{
          'type_paiement': 'espece',
          'motif': 'achat',
          'taux': 1
        }}
      >
        {/* Section de recherche du dépôt */}
        <Form.Item label="N° reçu dépôt" style={{ marginBottom: 10 }}>
          <Input.Group compact>
            <Form.Item
              name='numero_recu_depot'
              noStyle
            >
              <Input
                style={{ width: 'calc(100% - 100px)' }}
                placeholder="Saisissez le N° de reçu du dépôt (optionnel)"
              />
            </Form.Item>
            <Button 
              type="primary" 
              icon={<SearchOutlined />}
              loading={searchingDepot}
              onClick={searchDepotByNumero}
            >
            </Button>
          </Input.Group>
        </Form.Item>

        {/* Afficher les informations du dépôt si trouvé */}
        {depotFound && depotInfo && (
          <Form.Item>
            <div style={{ 
              padding: '10px', 
              backgroundColor: '#e6f7ff', 
              borderRadius: '4px',
              marginBottom: '10px',
              border: '1px solid #91d5ff'
            }}>
              <strong>Retrait de dépôt :</strong><br />
              Montant initial: <strong>{depotInfo.montant_sortie?.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} {depotInfo.devisesortie?.code}</strong><br />
              {depotInfo.nombre_retraits > 0 && (
                <>
                  Nombre de retraits: <strong>{depotInfo.nombre_retraits}</strong><br />
                  Total retiré: <strong style={{ color: 'red' }}>{depotInfo.total_retire?.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} {depotInfo.devisesortie?.code}</strong><br />
                </>
              )}
              Montant restant: <strong style={{ color: 'green' }}>{montantRestant.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} {depotInfo.devisesortie?.code}</strong>
            </div>
          </Form.Item>
        )}

        <Form.Item label="Numero reçu" style={{ marginBottom: 0 }} rules={[{ required: true }]}>
          <Form.Item
            name='numero_recu'
            style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
          >
            <Input
              placeholder="N° reçu du retrait"
              disabled={depotFound}
            />
          </Form.Item>
          <Form.Item name='date_operation' style={{ display: 'inline-block', width: 'calc(50% - 8px)', margin: '0 8px' }}  >
            <DatePicker 
              format="YYYY-MM-DD" 
              defaultValue={moment()} 
              placeholder="date" 
              style={{ width: '100%' }} 
              disabled={depotFound}
            />
          </Form.Item>
        </Form.Item>

        <Form.Item name='client_id' label="Clients" rules={[{ required: true }]}>
          <SelectSearchInput
            onChange={(v) => onChange('client', v)}
            data={clients}
            label='prenom'
            label2='nom'
            label3='telephone'
            valueLabel='id'
            onSearch={(value) => onSearch(value)}
            placeholder="Sélectionnez le client"
            disabled={depotFound}
          />
        </Form.Item>
        
        {/* Champs réceveur - TOUJOURS MODIFIABLES */}
        <Form.Item label="Réceveur" style={{ marginBottom: 0 }} rules={[{ required: true }]}>
          <Form.Item
            name='nom_deposant'
            style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
          >
            <Input placeholder="nom réceveur" />
          </Form.Item>
          <Form.Item
            name='tel_deposant'
            style={{ display: 'inline-block', width: 'calc(50% - 8px)', margin: '0 8px' }}
          >
            <Input placeholder="téléphone réceveur" />
          </Form.Item>
        </Form.Item>

        {/* Lieu de récupération - PRÉ-REMPLI mais MODIFIABLE */}
        <Form.Item name='pays_recuperation' label="Lieu de récupération" rules={[{ required: true, message: 'Lieu de récupération requis' }]}>
          <Select
            showSearch
            placeholder="Sélectionnez le lieu de récupération"
            disabled={false}
            optionFilterProp="children"
            filterOption={(input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {ITINERAIRES_LIST.map(itineraire => (
              <Option key={itineraire.code} value={itineraire.code}>
                {itineraire.nom}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/* Masquer le lieu de départ (récupéré du dépôt) */}
        <Form.Item name='pays_depart' noStyle>
          <Input type="hidden" />
        </Form.Item>

        {/* Type de paiement - PRÉ-REMPLI mais MODIFIABLE */}
        <Form.Item name='type_paiement' label="Type de paiement" rules={[{ required: true, message: 'Veuillez sélectionner le type de paiement' }]}>
          <Select placeholder="Sélectionnez le type de paiement" disabled={false}>
            <Option value="espece">Espèce</Option>
            <Option value="virement">Virement</Option>
            <Option value="depot_mobile">Dépôt mobile</Option>
            <Option value="cheque">Chèque</Option>
          </Select>
        </Form.Item>

        {/* Motif - PRÉ-REMPLI mais MODIFIABLE */}
        <Form.Item name='motif' label="Motif" rules={[{ required: true, message: 'Veuillez sélectionner le motif' }]}>
          <Select placeholder="Sélectionnez le motif" disabled={false}>
            <Option value="achat">Achat</Option>
            <Option value="vente">Vente</Option>
          </Select>
        </Form.Item>

        {/* Devises */}
        <Form.Item label="Devises *" style={{ marginBottom: 0 }} rules={[{ required: true }]}>
          {/* Devise d'entrée - VERROUILLÉE si dépôt trouvé */}
          <Form.Item
            name='devise_id'
            rules={[{ required: true }]}
            style={{ display: 'inline-block', width: 'calc(50% - 8px)' }}
          >
            <SelectSearchInput
              onChange={(v) => onDeviseChange('devise_id', v)}
              onSearch={(value) => onSearch(value)}
              label='code'
              valueLabel='code'
              data={devises}
              placeholder="Devise d'entrée"
              disabled={depotFound} // VERROUILLÉE si dépôt trouvé
            />
          </Form.Item>
          {/* Devise de sortie - PRÉ-REMPLIE mais MODIFIABLE */}
          <Form.Item
            name='devise_sortie'
            rules={[{ required: true }]}
            style={{ display: 'inline-block', width: 'calc(50% - 8px)', margin: '0 8px' }}
          >
            <SelectSearchInput
              onChange={(v) => onDeviseChange('devise_sortie', v)}
              onSearch={(value) => onSearch(value)}
              label='code'
              valueLabel='code'
              data={devises}
              placeholder="Devise de sortie"
              disabled={false} // TOUJOURS MODIFIABLE
            />
          </Form.Item>
        </Form.Item>

        {/* Taux - PRÉ-REMPLI mais MODIFIABLE */}
        <Form.Item name='taux' label="Le taux" rules={[{ required: true, message: 'Le taux est requis avant de saisir le montant' }]}>
          <InputNumber
            onChange={onTauxChange}
            placeholder="Saisissez le taux"
            style={{ width: '100%' }}
            formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value: any) => value.replace(/\$\s?|(,*)/g, '')}
          />
        </Form.Item>

        {/* Montant - AVEC VIRGULES SANS DÉCIMALES */}
        <Form.Item 
          name='montant' 
          label="Montant" 
          rules={[
            { type: 'number', required: true },
            { validator: validateMontant }
          ]}
          tooltip={!canEnterMontant && !depotFound ? "Veuillez d'abord saisir les devises et le taux" : ""}
          extra={depotFound && montantRestant > 0 ? `Maximum: ${montantRestant.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : ''}
        >
          <InputNumber
            disabled={!canEnterMontant && !depotFound}
            onChange={onMontantChange}
            placeholder={canEnterMontant || depotFound ? "Montant" : "Saisissez d'abord les devises et le taux"}
            style={{ width: '100%' }}
            max={depotFound ? montantRestant : undefined}
            formatter={value => {
              if (!value) return '';
              // Convertir en entier (sans décimales) puis ajouter les virgules
              return Math.floor(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }}
            parser={(value: any) => value.replace(/,/g, '')}
          />
        </Form.Item>

        {/* Montant converti - AVEC VIRGULES SANS DÉCIMALES */}
        <Form.Item
          name='montant_convert'
          label="M.Converti"
        >
          <InputNumber
            disabled
            placeholder="Montant converti"
            style={{ width: '100%' }}
            formatter={value => {
              if (!value) return '';
              // Convertir en entier (sans décimales) puis ajouter les virgules
              return Math.floor(Number(value)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }}
            parser={(value: any) => value.replace(/,/g, '')}
          />
        </Form.Item>
        
        <Form.Item initialValue='retrait' name='type_operation' noStyle>
          <Input type="hidden" />
        </Form.Item>

      </Form>
    </Modal>
  );
};

export default CollectionCreateForms;